import { Check, Globe } from "lucide-react";

import { cn } from "@/lib/utils";
import { useLocale } from "./context";
import type { Locale } from "./types";

const LOCALE_LABELS: Record<Locale, string> = {
  id: "Bahasa Indonesia",
  en: "English",
};

const LOCALE_ORDER: ReadonlyArray<Locale> = ["id", "en"];

interface LocaleSwitcherProps {
  /**
   * Invoked after a locale is selected — used by the user menu
   * to close its dropdown once the choice is made.
   */
  onSelect?: () => void;
}

/**
 * Locale options rendered as menu items inside the sidebar user
 * menu dropdown. Each option carries ``role="menuitem"`` to match
 * the surrounding menu semantics; the active locale is marked
 * with a check icon.
 */
export function LocaleSwitcher({ onSelect }: LocaleSwitcherProps = {}) {
  const { locale, setLocale } = useLocale();

  return (
    <div role="group" aria-label="Bahasa antarmuka">
      <div className="flex items-center gap-2 px-3 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-ocean-600">
        <Globe size={12} className="text-ocean-400" />
        Bahasa
      </div>
      {LOCALE_ORDER.map((option) => {
        const isActive = option === locale;
        return (
          <button
            key={option}
            type="button"
            role="menuitemradio"
            aria-checked={isActive}
            onClick={() => {
              if (!isActive) setLocale(option);
              onSelect?.();
            }}
            className={cn(
              "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-ocean-50",
              isActive ? "font-medium text-ocean-800" : "text-neutral-600"
            )}
          >
            {LOCALE_LABELS[option]}
            {isActive && <Check size={14} className="text-ocean-500" />}
          </button>
        );
      })}
    </div>
  );
}
